// portfolioData.ts - Static data from Yug Patil's Resume

import { ProfileBanner, TimelineItem, Project, Certification, ContactMe, Skill, WorkPermit } from '../types';

export const profileBannerData: ProfileBanner = {
  backgroundImage: { url: '' },
  headline: "Yug Patil",
  resumeLink: { url: '/Yug-Resume.pdf' },
  linkedinLink: "https://www.linkedin.com/in/yugkp44/", // Update with your LinkedIn
  profileSummary: "Full Stack Developer | React.js | Node.js | MongoDB | AWS | Building scalable web applications with modern technologies"
};

export const contactMeData: ContactMe = {
  profilePicture: { url: '' }, // Add your profile picture URL
  name: "Yug Patil",
  title: "Full Stack Developer",
  summary: "Passionate full-stack developer with expertise in React.js, Node.js, MongoDB, and AWS. Experienced in building scalable web applications and microservices.",
  companyUniversity: "D.Y. Patil Institute of Technology, Pune",
  linkedinLink: "https://www.linkedin.com/in/yugkp44/",
  email: "yugkpatil44@gmail.com",
  phoneNumber: "+91-8140406584"
};

export const timelineData: TimelineItem[] = [
  {
    timelineType: 'work',
    name: 'Tabnet Security Pvt. Ltd.',
    title: 'Software Developer Intern',
    techStack: 'React Native, React.js, Data Visualization, Analytics',
    summaryPoints: [
      "Developed the company's complete mobile application using React Native",
      'Built data visualization dashboards with React.js',
      'Implemented analytics features for security insights and performance reporting',
      'Created interactive charts and graphs for data-driven decision making'
    ],
    dateRange: 'Jul 2025 - Present'
  },
  {
    timelineType: 'work',
    name: 'Creatosaurus Pvt. Ltd.',
    title: 'Full Stack Developer Intern',
    techStack: 'React.js, Node.js, MongoDB, AI Integration, Analytics',
    summaryPoints: [
      'Built web applications with integrated analytics and AI-driven workflow optimization features',
      'Improved system efficiency through data analysis',
      'Developed full-stack features using modern JavaScript technologies',
      'Collaborated on AI-powered automation solutions'
    ],
    dateRange: 'Dec 2024 - May 2025'
  },
  {
    timelineType: 'education',
    name: 'D.Y. Patil Institute of Technology, Pune',
    title: 'B.E in Electronics & Telecommunication',
    techStack: '',
    summaryPoints: [
      'CGPA: 8.66',
      'Specialization in Full Stack Development',
      'Active participation in coding competitions and hackathons'
    ],
    dateRange: '2022 - 2026'
  },
  {
    timelineType: 'education',
    name: 'A B School, Navsari, Gujarat',
    title: 'Class XII',
    techStack: '',
    summaryPoints: [
      'Percentage: 78%',
      'Science Stream'
    ],
    dateRange: '2020 - 2022'
  }
];

export const projectsData: Project[] = [
  {
    title: 'Netflix Portfolio',
    description: 'A Netflix-themed portfolio website built with React.js, TypeScript, and modern UI/UX design principles. Features smooth animations, responsive design, and dynamic content loading.',
    techUsed: 'React, TypeScript, CSS3, Node.js',
    image: { url: 'https://images.unsplash.com/photo-1574375927938-d5a98e8ffe85?w=400' }
  },
  {
    title: 'RoomLink - Brokerfree Rental Platform',
    description: 'Built full-stack room rental platform with React frontend and Express.js backend for broker-free property listings. Implemented JWT authentication with bcrypt hashing and protected API endpoints. Developed interactive map visualization using React Leaflet and image upload features. Deployed on Vercel (frontend) and Railway (backend) with Docker containerization.',
    techUsed: 'React, Vite, Node.js, Express.js, MongoDB, Docker',
    image: { url: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=400' }
  },
  {
    title: 'GST & Income Tax Document Processing System',
    description: 'Built end-to-end RAG pipeline processing 100+ official tax documents, creating semantic search system with FAISS embeddings to analyze and retrieve relevant tax law information. Developed document analysis system using NLP techniques and vector similarity matching, enabling efficient information extraction from complex government PDFs and legal text.',
    techUsed: 'Python, FAISS, Transformers, Flask, RAG, NLP',
    image: { url: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400' }
  },
  {
    title: 'SustainAI: AI Carbon Footprint Tracker',
    description: 'Built a serverless pipeline ingesting 50K+ kWh readings/day. Designed interactive Looker Studio dashboards with filters and automated daily PDF reports for stakeholders. Configured Cloud Monitoring alerts for energy spikes and pipeline errors, reducing downtime by 30%. Enabled pilot teams to cut AI carbon emissions by 12%.',
    techUsed: 'Python, Apache Beam, Looker Studio, GCP',
    image: { url: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=400' }
  }
];

export const skillsData: Skill[] = [
  // Frontend
  { name: 'React.js', category: 'Frontend', description: 'Building interactive UIs with React hooks and components', icon: 'FaReact' },
  { name: 'React Native', category: 'Frontend', description: 'Cross-platform mobile app development', icon: 'FaReact' },
  { name: 'Expo', category: 'Frontend', description: 'React Native development platform and tools', icon: 'FaReact' },
  { name: 'TypeScript', category: 'Frontend', description: 'Type-safe JavaScript development', icon: 'SiTypescript' },
  { name: 'HTML5', category: 'Frontend', description: 'Semantic markup and modern HTML features', icon: 'SiHtml5' },
  { name: 'CSS3', category: 'Frontend', description: 'Responsive design, animations, and flexbox/grid', icon: 'SiCss3' },
  { name: 'JavaScript', category: 'Frontend', description: 'ES6+ features, async programming, DOM manipulation', icon: 'FaNodeJs' },

  // Backend
  { name: 'Node.js', category: 'Backend', description: 'Server-side JavaScript runtime', icon: 'FaNodeJs' },
  { name: 'Express.js', category: 'Backend', description: 'Fast, minimalist web framework for Node.js', icon: 'FaNodeJs' },
  { name: 'MongoDB', category: 'Backend', description: 'NoSQL database for modern applications', icon: 'SiPostgresql' },
  { name: 'REST APIs', category: 'Backend', description: 'Designing and implementing RESTful services', icon: 'FaNodeJs' },
  { name: 'Flask', category: 'Backend', description: 'Python web framework for APIs', icon: 'FaNodeJs' },

  // Tools & DevOps
  { name: 'Git', category: 'Tools & DevOps', description: 'Version control and collaboration', icon: 'FaGitAlt' },
  { name: 'Docker', category: 'Tools & DevOps', description: 'Containerization and deployment', icon: 'FaDocker' },
  { name: 'GCP', category: 'Tools & DevOps', description: 'Google Cloud Platform services', icon: 'SiGooglecloud' },
  { name: 'Vercel', category: 'Tools & DevOps', description: 'Frontend deployment and hosting', icon: 'FaNodeJs' },

  // Languages & AI/ML
  { name: 'Python', category: 'Languages', description: 'Scripting, automation, and AI/ML', icon: 'FaNodeJs' },
  { name: 'Java', category: 'Languages', description: 'Object-oriented programming and enterprise applications', icon: 'FaJava' },
  { name: 'C++', category: 'Languages', description: 'Data structures, algorithms, and system programming', icon: 'FaNodeJs' },
  { name: 'FAISS', category: 'AI/ML', description: 'Vector similarity search and embeddings', icon: 'FaNodeJs' },
  { name: 'NLP', category: 'AI/ML', description: 'Natural Language Processing techniques', icon: 'FaNodeJs' },
  { name: 'RAG', category: 'AI/ML', description: 'Retrieval Augmented Generation pipelines', icon: 'FaNodeJs' }
];

export const certificationsData: Certification[] = [
  {
    title: 'C++ Programming',
    issuer: 'IIT Bombay (NPTEL)',
    issuedDate: '2024',
    link: 'https://drive.google.com/file/d/1yf5o8MtaLNrz7Jz6busWIqhQM6MWnVJ0/view',
    iconName: 'university'
  },
  {
    title: 'Java Programming',
    issuer: 'IIT Kharagpur (NPTEL)',
    issuedDate: '2024',
    link: 'https://drive.google.com/file/d/1LZz2R47c7_Y0f0fkUKqVfyuLys9kPJQF/view',
    iconName: 'university'
  },
  {
    title: 'Cybersecurity Analyst Job Simulation',
    issuer: 'TATA',
    issuedDate: '2024',
    link: 'https://drive.google.com/file/d/1pxeaNsHBhsfND5PzCAP_7ynXtCxgPfqQ/view',
    iconName: 'university'
  }
];

export const workPermitData: WorkPermit = {
  visaStatus: 'Indian Citizen',
  expiryDate: new Date('2030-12-31'),
  summary: 'Available for full-time opportunities',
  additionalInfo: 'Open to relocation and remote work'
};
